import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Cookie, X } from 'lucide-react';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Small delay for better UX
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const acceptNecessary = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      
      {/* Banner */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6 sm:p-8 animate-fade-in-up">
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-sun-orange/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Cookie className="w-6 h-6 text-sun-orange" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              Cookie-Einstellungen
            </h3>
            <p className="text-gray-600 text-sm">
              Wir respektieren Ihre Privatsphäre
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            Wir verwenden Cookies, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. 
            Sie können wählen, welche Cookies Sie zulassen möchten.
          </p>

          {/* Details Toggle */}
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-sun-blue text-sm font-medium hover:underline"
          >
            {showDetails ? 'Details ausblenden' : 'Details anzeigen'}
          </button>

          {/* Cookie Details */}
          {showDetails && (
            <div className="mt-4 space-y-3 text-sm">
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900">Notwendige Cookies</span>
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">Immer aktiv</span>
                </div>
                <p className="text-gray-600 text-xs">
                  Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden.
                </p>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900">Analyse-Cookies</span>
                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Optional</span>
                </div>
                <p className="text-gray-600 text-xs">
                  Helfen uns zu verstehen, wie Besucher mit der Website interagieren.
                </p>
              </div>
              
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-gray-900">Marketing-Cookies</span>
                  <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Optional</span>
                </div>
                <p className="text-gray-600 text-xs">
                  Werden verwendet, um Besuchern relevante Werbung anzuzeigen.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={acceptNecessary}
            variant="outline"
            className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Nur notwendige
          </Button>
          <Button
            onClick={acceptAll}
            className="flex-1 bg-sun-orange hover:bg-sun-orange-dark text-white"
          >
            Alle akzeptieren
          </Button>
        </div>

        {/* Privacy Link */}
        <p className="text-xs text-gray-500 text-center mt-4">
          Mit der Nutzung unserer Website erklären Sie sich mit unserer{' '}
          <a href="#datenschutz" className="text-sun-blue hover:underline">Datenschutzerklärung</a>
          {' '}einverstanden.
        </p>
      </div>
    </div>
  );
};

export default CookieBanner;
